import type { ReactNode } from "react";
import { Pressable, StyleSheet } from "react-native";

import { webControlHoverTransition } from "@/design/interactionMotion";
import { Text } from "@/design/Text";
import { hoverRingStrong, palette, radius, space } from "@/design/tokens";

type UtilityChipProps = {
  accessibilityLabel?: string;
  icon?: ReactNode;
  label: string;
  onPress: () => void;
};

/** Compact section-header action used for low-emphasis utility controls. */
export function UtilityChip({
  accessibilityLabel,
  icon,
  label,
  onPress
}: UtilityChipProps) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      onPress={onPress}
      style={({ hovered, focused, pressed }) => [
        styles.base,
        (hovered || focused) && styles.hovered,
        pressed && styles.pressed
      ]}
    >
      {icon}
      <Text variant="smallStrong" color={palette.muted}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    alignItems: "center",
    backgroundColor: palette.coolWash,
    borderColor: "transparent",
    borderRadius: radius.pill,
    borderWidth: 1,
    flexDirection: "row",
    gap: 6,
    paddingHorizontal: space.md,
    paddingVertical: 6,
    ...webControlHoverTransition
  },
  hovered: {
    backgroundColor: palette.coolWashHover,
    ...hoverRingStrong
  },
  pressed: {
    backgroundColor: palette.coolWashHover,
    opacity: 0.85
  }
});
